// The contractor's look-ahead, waiting for a yes.
//
// The workbook arrives as a Doc Control upload and is read straight away
// (utils/lookaheadRun); what it WOULD do sits here until someone applies
// it. Nothing about it is automatic, and the card says so, because the
// dates it writes are the ones the field and BES work to: the field
// forecast on every area row of a level and system, the shop drawing due
// one working day before that start, and the dependency between them.
//
// It shows nothing at all when there is nothing waiting - a card that is
// always there is a card people stop reading.
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Modal, Popconfirm, Space, Table, Tag, Tooltip, message } from "antd";
import { applyLookaheadRun, dismissLookaheadRun, getLookaheadRun, getLookaheadRuns } from "api/tracking/trackingElementsApi";

const T = { muted: "#6B7280", line: "#E5E7EB" };
const day = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");
const n0 = (v) => Number(v || 0).toLocaleString();

export default function LookaheadRunCard({ projectId, canApply = false, onApplied = null }) {
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) { setRun(null); return; }
    try {
      const r = await getLookaheadRuns(projectId);
      // The newest one still waiting. An applied or dismissed run is
      // history; it belongs in the document's own trail, not in the way.
      setRun((r.runs || []).find((x) => x.state === "proposed" || x.state === "failed") || null);
    } catch (e) { setRun(null); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  if (!run) return null;

  const c = run.counts || {};
  const nothing = !c.forecasts && !c.gates && !c.newRows;

  const openPlan = async () => {
    try {
      const r = await getLookaheadRun(projectId, run._id);
      setPlan(r.run && r.run.plan ? r.run.plan : null);
    } catch (e) { message.error(e.message || "Could not read that plan"); }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const r = await applyLookaheadRun(projectId, run._id);
      message.success(`Applied: ${n0(r.forecasts)} forecast${r.forecasts === 1 ? "" : "s"}, ${n0(r.deadlines)} drawing deadline${r.deadlines === 1 ? "" : "s"}, ${n0(r.dependencies)} dependenc${r.dependencies === 1 ? "y" : "ies"}`);
      setRun(null);
      if (onApplied) onApplied();
    } catch (e) {
      message.error(e.message || "Could not apply that look-ahead");
    } finally { setBusy(false); }
  };

  const dismiss = async () => {
    setBusy(true);
    try { await dismissLookaheadRun(projectId, run._id); setRun(null); }
    catch (e) { message.error(e.message || "Could not put that aside"); }
    finally { setBusy(false); }
  };

  if (run.state === "failed") {
    return (
      <Alert
        type="warning" showIcon style={{ marginBottom: 12 }}
        message={`"${run.fileName}" was filed, but its look-ahead sheet could not be read`}
        description={<span style={{ fontSize: 12 }}>{run.error || "The workbook has no \"6-Week Look-Ahead Days\" sheet."} The document itself is in Document Control; nothing on the schedule has changed.</span>}
        action={canApply ? <Button size="small" onClick={dismiss} loading={busy}>Put aside</Button> : null}
      />
    );
  }

  return (
    <>
      <Card
        size="small"
        style={{ marginBottom: 12, borderColor: "#B7D3F2" }}
        bodyStyle={{ padding: "8px 12px" }}
        title={<span style={{ fontSize: 13 }}>Contractor look-ahead · <span style={{ color: T.muted, fontWeight: 400 }}>{run.fileName}</span></span>}
        extra={(
          <Space size={6}>
            <Button size="small" onClick={openPlan}>See what it would do</Button>
            {canApply && (
              <>
                <Popconfirm
                  title="Apply this look-ahead?"
                  description={(
                    <div style={{ maxWidth: 380, fontSize: 12 }}>
                      It writes the field forecast on {n0(c.forecasts)} row{c.forecasts === 1 ? "" : "s"} and a shop-drawing deadline on {n0(c.gates)},
                      one working day before each forecast start. Planned and actual dates are not touched, and the {n0(c.newRows)} proposed
                      row{c.newRows === 1 ? "" : "s"} are not created. The rows are backed up first.
                    </div>
                  )}
                  okText="Apply the look-ahead" cancelText="Not yet" onConfirm={apply}
                >
                  <Button size="small" type="primary" loading={busy} disabled={nothing}>Apply</Button>
                </Popconfirm>
                <Button size="small" onClick={dismiss} loading={busy}>Put aside</Button>
              </>
            )}
          </Space>
        )}
      >
        <Space size={6} wrap style={{ fontSize: 12 }}>
          <Tag>as of {day(run.asOf)}</Tag>
          <Tag color="blue">{n0(c.forecasts)} field forecast{c.forecasts === 1 ? "" : "s"}</Tag>
          <Tag color="blue">{n0(c.gates)} drawing deadline{c.gates === 1 ? "" : "s"}</Tag>
          {c.lateAgainstLookahead ? <Tag color="red">{n0(c.lateAgainstLookahead)} drawing{c.lateAgainstLookahead === 1 ? "" : "s"} late against it</Tag> : null}
          {c.missingDrawings ? <Tag color="orange">{n0(c.missingDrawings)} gated system{c.missingDrawings === 1 ? "" : "s"} with no drawing row</Tag> : null}
          {c.newRows ? <Tooltip title="Work in the look-ahead with no field row on the schedule. These are listed, never created."><Tag>{n0(c.newRows)} proposed row{c.newRows === 1 ? "" : "s"}</Tag></Tooltip> : null}
          <span style={{ color: T.muted }}>Read when the workbook was filed. Nothing has moved yet.</span>
        </Space>
      </Card>

      <Modal open={!!plan} onCancel={() => setPlan(null)} footer={null} width={900}
             title={`What "${run.fileName}" would do`}>
        {plan ? (
          <div style={{ maxHeight: "calc(60 * var(--vh))", overflow: "auto" }}>
            <Section title={`Field forecasts (${(plan.forecasts || []).length})`} rows={plan.forecasts} columns={[
              { title: "Row", dataIndex: "name", ellipsis: true },
              { title: "Planned", key: "p", width: 190, render: (_, r) => `${(r.current && r.current.start) || "-"} → ${(r.current && r.current.end) || "-"}` },
              { title: "Forecast", key: "f", width: 190, render: (_, r) => `${r.forecastStart} → ${r.forecastFinish}` },
            ]} />
            <Section title={`Shop-drawing deadlines (${(plan.gates || []).length})`} rows={plan.gates} columns={[
              { title: "Drawing", dataIndex: "name", ellipsis: true },
              { title: "Due by", dataIndex: "dueBy", width: 110 },
              { title: "State", dataIndex: "state", width: 210 },
            ]} />
            <Section title={`Gated systems with no drawing row (${(plan.missingDrawings || []).length})`} rows={plan.missingDrawings} columns={[
              { title: "Field row", dataIndex: "name", ellipsis: true },
              { title: "Package", dataIndex: "pkg", width: 160 },
              { title: "A drawing would be due by", dataIndex: "dueBy", width: 200 },
            ]} />
            <Section title={`Proposed new rows (${(plan.newRows || []).length}, not created)`} rows={plan.newRows} columns={[
              { title: "Would be", dataIndex: "proposedName", ellipsis: true },
              { title: "Forecast", key: "f", width: 190, render: (_, r) => `${r.forecastStart} → ${r.forecastFinish}` },
              { title: "From", dataIndex: "desc", ellipsis: true },
            ]} />
          </div>
        ) : null}
      </Modal>
    </>
  );
}

function Section({ title, rows, columns }) {
  if (!rows || !rows.length) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 4 }}>{title}</div>
      <Table size="small" rowKey={(r, i) => `${r.row || r.drawing || r.proposedName || i}`}
             dataSource={rows} columns={columns} pagination={false}
             style={{ border: `1px solid ${T.line}`, borderRadius: 4 }} />
    </div>
  );
}
